import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { UserCircle2, Delete, X, RotateCcw } from "lucide-react";

/**
 * Pulsante mostrato nel header dei POS che apre un modale per il cambio operatore tramite PIN.
 * Su PIN valido aggiorna token e operatore senza ricaricare la pagina.
 * Nell'APK un reload di /cassa/... lascia il WebView bianco.
 */
export default function OperatorSwitcher({ compact = false }) {
  const { user, setSession } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const press = (k) => {
    if (busy) return;
    if (k === "C") setPin("");
    else if (k === "←") setPin(pin.slice(0, -1));
    else if (pin.length < 6) {
      const next = pin + k;
      setPin(next);
      if (next.length === 4) setTimeout(() => document.querySelector('[data-testid="operator-switch-submit"]')?.click(), 50);
    }
  };

  const submit = async () => {
    if (pin.length < 4) return toast.error("PIN almeno 4 cifre");
    setBusy(true);
    try {
      const r = await api.post("/auth/pin-switch", { pin });
      const { token: newToken, user: newUser } = r.data;
      setSession(newToken, newUser);
      toast.success(`Operatore: ${newUser?.name || "cambio eseguito"}`);
      setOpen(false);
      setPin("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN errato");
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="operator-switch-btn"
        onClick={() => setOpen(true)}
        title={`Operatore corrente: ${user.name}`}
        className={`inline-flex items-center gap-2 ${compact ? "h-9 px-3 text-xs" : "h-10 px-4 text-sm"} bg-white border border-velta-border rounded-md font-medium hover:border-velta-ink transition`}
      >
        <UserCircle2 className="w-4 h-4 text-[#2F9E77]" />
        <span className="hidden md:inline">{user.name}</span>
        <span className="md:hidden">{user.name?.split(" ")[0]}</span>
        <RotateCcw className="w-3 h-3 text-velta-mute" />
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-velta-border flex items-center justify-between">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Cambio operatore</div>
                <div className="font-heading font-semibold text-lg">Inserisci il PIN</div>
              </div>
              <button data-testid="operator-switch-close" onClick={() => { setOpen(false); setPin(""); }}><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5">
              <div className="bg-velta-bg border border-velta-border rounded-md py-6 mb-4 text-center">
                <div className="font-mono font-bold text-5xl tracking-[0.6em] pl-2" data-testid="operator-switch-display">
                  {pin.padEnd(4, "·").split("").map((c, i) => (
                    <span key={i} className={pin[i] ? "text-velta-ink" : "text-velta-mute/40"}>
                      {pin[i] ? "•" : "·"}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {["1","2","3","4","5","6","7","8","9","C","0","←"].map(k => (
                  <button
                    key={k}
                    data-testid={`operator-key-${k}`}
                    onClick={() => press(k)}
                    disabled={busy}
                    className={`h-14 rounded-md font-mono font-bold text-2xl btn-tactile transition disabled:opacity-40 ${
                      k === "C" ? "bg-red-50 text-red-700 border border-red-200" :
                      k === "←" ? "bg-velta-bg border border-velta-border" :
                      "bg-white border border-velta-border hover:border-velta-ink"
                    }`}
                  >
                    {k === "←" ? <Delete className="w-5 h-5 mx-auto" /> : k}
                  </button>
                ))}
              </div>

              <button
                data-testid="operator-switch-submit"
                onClick={submit}
                disabled={pin.length < 4 || busy}
                className="mt-4 w-full h-14 bg-[#2F9E77] hover:bg-[#27855F] text-white rounded-md font-semibold disabled:opacity-40 btn-tactile"
              >
                {busy ? "Verifica…" : "Cambia operatore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
